import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Radar, Activity, TrendingUp, Target, Loader2, RefreshCw, Zap,
  MapPin, Building2, Calendar, ChevronRight, Info, Newspaper, ClipboardList, FileCode,
  ExternalLink, Hash, Mail, X as XIcon,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  listPretenderSignals, listPretenderSources, collectPretenderSignals,
  listPretenderCandidates, getPretenderCandidate, getPretenderStats,
} from '../lib/api';

const SIGNAL_TYPE_LABELS = {
  tender_plan: { label: 'Plan zamówień', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30', icon: ClipboardList },
  project_documentation: { label: 'Dokumentacja projektowa', color: 'bg-purple-500/15 text-purple-300 border-purple-500/30', icon: FileCode },
  news_mention: { label: 'Prasa', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: Newspaper },
  market_consultation: { label: 'Konsultacje rynkowe', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: Zap },
  environmental_decision: { label: 'Decyzja środowiskowa', color: 'bg-teal-500/15 text-teal-300 border-teal-500/30', icon: Info },
  building_permit: { label: 'Pozwolenie bud.', color: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30', icon: Info },
  eu_grant_awarded: { label: 'Dotacja UE', color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30', icon: Info },
};

function StatsCards({ stats }) {
  if (!stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card>
        <CardContent className="pt-5">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Activity className="w-3.5 h-3.5" /> Sygnały
          </div>
          <div className="text-2xl font-bold">{stats.total_signals || 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Target className="w-3.5 h-3.5" /> Kandydaci
          </div>
          <div className="text-2xl font-bold">{stats.total_candidates || 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> Hot (≥60)
          </div>
          <div className="text-2xl font-bold text-emerald-400">{stats.hot_candidates || 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Radar className="w-3.5 h-3.5" /> Źródła
          </div>
          <div className="text-2xl font-bold">{(stats.by_source || []).length}</div>
        </CardContent>
      </Card>
    </div>
  );
}

function CandidateCard({ cand, onClick }) {
  const prob = Math.round((cand.probability_12m || 0) * 100);
  const probColor =
    prob >= 70 ? 'text-emerald-400' :
    prob >= 40 ? 'text-amber-400' : 'text-muted-foreground';

  return (
    <Card
      className="hover:border-primary/40 transition cursor-pointer hover:shadow-md"
      onClick={() => onClick && onClick(cand)}
      title="Kliknij aby zobaczyć szczegóły kandydata"
    >
      <CardContent className="pt-4 pb-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm line-clamp-2">{cand.title}</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
              {cand.region && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{cand.region}</span>}
              {cand.entity_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{cand.entity_name.substring(0, 40)}</span>}
              <span>{cand.signal_count} sygnałów</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl font-bold">{Math.round(cand.score)}</div>
            <div className={`text-xs ${probColor}`}>{prob}% w 12m</div>
            <ChevronRight className="w-3 h-3 ml-auto text-muted-foreground mt-1" />
          </div>
        </div>

        <Progress value={cand.score} className="h-1.5" />

        <div className="flex flex-wrap gap-1">
          {(cand.signal_types_seen || []).slice(0, 5).map(t => {
            const meta = SIGNAL_TYPE_LABELS[t] || { label: t, color: 'bg-muted' };
            return (
              <Badge key={t} variant="outline" className={`${meta.color} text-xs`}>
                {meta.label}
              </Badge>
            );
          })}
        </div>

        {cand.predicted_tender_start && (
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Prognoza: {cand.predicted_tender_start.substring(0, 10)}
            {cand.predicted_tender_end && ` – ${cand.predicted_tender_end.substring(0, 10)}`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Detail dialog for a candidate — fix dla BUG #2 (UI audit 2026-05-10) + materialization (2026-05-11)
function CandidateDetailDialog({ candidateId, open, onClose }) {
  const [cand, setCand] = useState(null);
  const [relatedSignals, setRelatedSignals] = useState([]);
  const [materialization, setMaterialization] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !candidateId) return;
    setLoading(true);
    getPretenderCandidate(candidateId)
      .then(d => {
        // Backend returns {candidate, signals, materialization} — destructure properly
        setCand(d.candidate || d);
        setRelatedSignals(d.signals || []);
        setMaterialization(d.materialization || null);
      })
      .catch(() => {
        setCand(null);
        setRelatedSignals([]);
        setMaterialization(null);
      })
      .finally(() => setLoading(false));
  }, [open, candidateId]);

  if (!open) return null;

  const prob = Math.round((cand?.probability_12m || 0) * 100);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-4 h-4" /> Pre-Tender Candidate
            {cand && <Badge variant="outline" className="text-[10px]">score {Math.round(cand.score)}</Badge>}
            {cand && <Badge variant="outline" className="text-[10px]">{prob}% w 12m</Badge>}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            {cand?.title || 'Ładuję...'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : !cand ? (
          <div className="text-center py-6 text-muted-foreground">
            Nie udało się załadować szczegółów.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Entity info */}
            <Card>
              <CardContent className="pt-4 pb-4 space-y-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Inwestor</div>
                <div className="font-semibold">{cand.entity_name || '—'}</div>
                <div className="text-xs flex items-center gap-3 flex-wrap mt-1">
                  {cand.entity_nip && <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> NIP: {cand.entity_nip}</span>}
                  {cand.region && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {cand.region}</span>}
                  {cand.city && <span>📍 {cand.city}</span>}
                </div>
              </CardContent>
            </Card>

            {/* Predicted tender window */}
            {cand.predicted_tender_start && (
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Prognozowane okno przetargu</div>
                  <div className="flex items-center gap-2 mt-1 text-sm">
                    <Calendar className="w-4 h-4" />
                    <strong>{cand.predicted_tender_start.substring(0, 10)}</strong>
                    {cand.predicted_tender_end && (
                      <> – <strong>{cand.predicted_tender_end.substring(0, 10)}</strong></>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CPV codes expected */}
            {cand.cpv_codes_expected && cand.cpv_codes_expected.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">CPV expected</div>
                <div className="flex flex-wrap gap-1">
                  {cand.cpv_codes_expected.map(cpv => (
                    <Badge key={cpv} variant="outline" className="text-[10px]">{cpv}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Estimated value — BUG #3 fix (2026-05-11): zawsze pokazuj sekcję, message gdy brak */}
            <Card>
              <CardContent className="pt-3 pb-3">
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Wartość szacowana (PLN)</div>
                {(cand.estimated_value_min || cand.estimated_value_expected || cand.estimated_value_max) ? (
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] text-muted-foreground">Min</div>
                      <div>{cand.estimated_value_min?.toLocaleString('pl-PL') || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Expected</div>
                      <div className="font-semibold">{cand.estimated_value_expected?.toLocaleString('pl-PL') || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground">Max</div>
                      <div>{cand.estimated_value_max?.toLocaleString('pl-PL') || '—'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">
                    Brak danych — sygnały źródłowe nie zawierały wartości budżetowej.
                    Wartość zostanie określona po publikacji SWZ.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signal types badges */}
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Typy sygnałów</div>
              <div className="flex flex-wrap gap-1">
                {(cand.signal_types_seen || []).map(t => {
                  const meta = SIGNAL_TYPE_LABELS[t] || { label: t, color: 'bg-muted' };
                  const Icon = meta.icon || Info;
                  return (
                    <Badge key={t} variant="outline" className={`${meta.color} text-xs`}>
                      <Icon className="w-3 h-3 mr-1" /> {meta.label}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Related signals — clickable list */}
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Sygnały źródłowe ({cand.signal_count || 0})
              </div>
              {relatedSignals.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">
                  Brak załadowanych sygnałów (mogą być nieaktualne lub poza scope listy).
                </div>
              ) : (
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {relatedSignals.map(sig => {
                    const meta = SIGNAL_TYPE_LABELS[sig.signal_type] || { label: sig.signal_type, color: 'bg-muted' };
                    const Icon = meta.icon || Info;
                    return (
                      <div key={sig.id} className="border rounded p-2 text-xs space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="outline" className={`${meta.color} text-[10px]`}>
                            <Icon className="w-2.5 h-2.5 mr-1" /> {meta.label}
                          </Badge>
                          {sig.event_date && (
                            <span className="text-[10px] text-muted-foreground">
                              {sig.event_date.substring(0, 10)}
                            </span>
                          )}
                        </div>
                        <div className="line-clamp-2">{sig.title}</div>
                        {sig.source_url && (
                          <a href={sig.source_url} target="_blank" rel="noopener noreferrer"
                             className="text-primary text-[10px] flex items-center gap-0.5">
                            <ExternalLink className="w-2.5 h-2.5" /> {sig.source_name}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Materialization — FIX #2 (2026-05-11) + BUG #3 (2026-05-11): zawsze pokazuj sekcję */}
            <Card className={
              materialization?.linked ? 'border-emerald-500/30' :
              (materialization?.potential || []).length > 0 ? 'border-amber-500/30' : ''
            }>
              <CardContent className="pt-3 pb-3 space-y-2">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Materializacja w opportunities
                </div>
                {materialization && (materialization.linked || (materialization.potential || []).length > 0) ? (
                  materialization.linked ? (
                    <>
                      <div className="text-xs text-emerald-400 uppercase font-medium">
                        ✅ Zmaterializowany jako tender (confidence {Math.round(materialization.confidence * 100)}%)
                      </div>
                      <a href={`/tenders/${encodeURIComponent(materialization.linked.source_id)}`}
                         className="text-primary text-sm flex items-center gap-1 hover:underline">
                        <ExternalLink className="w-3 h-3" />
                        [{materialization.linked.source?.toUpperCase()}] {materialization.linked.title?.slice(0, 80)}
                      </a>
                      <div className="text-[10px] text-muted-foreground flex gap-3">
                        {materialization.linked.deadline && (
                          <span>deadline: {materialization.linked.deadline.slice(0, 10)}</span>
                        )}
                        {materialization.linked.pipeline_stage && (
                          <span>stage: {materialization.linked.pipeline_stage}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs text-amber-400 uppercase font-medium">
                        🟡 Potencjalne dopasowania ({materialization.potential.length}) — match: {materialization.match_type}, conf {Math.round((materialization.confidence || 0) * 100)}%
                      </div>
                      <div className="space-y-1">
                        {materialization.potential.map((opp) => (
                          <a key={opp.source_id}
                             href={`/tenders/${encodeURIComponent(opp.source_id)}`}
                             className="block text-sm text-primary hover:underline truncate"
                             title={opp.title}>
                            <ExternalLink className="w-3 h-3 inline mr-1" />
                            [{opp.source?.toUpperCase()}] {opp.title?.slice(0, 70)}
                          </a>
                        ))}
                      </div>
                      <div className="text-[10px] text-muted-foreground italic">
                        💡 Auto-detekcja po NIP/nazwie/CPV. Może być fałszywy alarm — sprawdź ręcznie.
                      </div>
                    </>
                  )
                ) : (
                  <div className="text-xs text-muted-foreground italic">
                    🔍 Brak dopasowań w bazie opportunities (sprawdzono: NIP, nazwę, CPV).
                    {' '}Kandydat nie zmaterializował się jeszcze jako tender — pojawi się tu gdy
                    BZP ogłosi konkretne postępowanie.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status */}
            {cand.status && (
              <div className="text-xs">
                Status: <Badge variant="outline" className="text-[10px]">{cand.status}</Badge>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Zamknij</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignalRow({ sig }) {
  const meta = SIGNAL_TYPE_LABELS[sig.signal_type] || { label: sig.signal_type, color: 'bg-muted' };
  const Icon = meta.icon || Info;
  return (
    <TableRow>
      <TableCell>
        <Badge variant="outline" className={`${meta.color} text-xs`}>
          <Icon className="w-3 h-3 mr-1" />
          {meta.label}
        </Badge>
      </TableCell>
      <TableCell className="max-w-md">
        <div className="text-sm line-clamp-2">{sig.title}</div>
        {sig.entity_name && (
          <div className="text-xs text-muted-foreground">{sig.entity_name}</div>
        )}
      </TableCell>
      <TableCell className="text-xs">{sig.region || '—'}</TableCell>
      <TableCell className="text-xs">{sig.source_name}</TableCell>
      <TableCell className="text-xs">
        {sig.event_date ? sig.event_date.substring(0, 10) : '—'}
      </TableCell>
      <TableCell>
        {sig.source_url && (
          <a
            href={sig.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline text-xs"
          >
            Źródło →
          </a>
        )}
      </TableCell>
    </TableRow>
  );
}

function SourcesPanel({ sources, onRefresh, collecting }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Źródła sygnałów</h3>
        <Button onClick={onRefresh} size="sm" disabled={collecting}>
          {collecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Zbierz teraz
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(sources || []).map(s => (
          <Card key={s.name}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">{s.name}</span>
                <Badge variant="outline" className="text-xs">{s.base_weight}p</Badge>
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{s.description}</div>
              <div className="flex items-center justify-between text-xs">
                <span>Sygnały: <strong>{s.count || 0}</strong></span>
                {s.last_detected && (
                  <span className="text-muted-foreground">
                    {s.last_detected.substring(0, 10)}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function PreTender({ lang, tenantId }) {
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  // BUG #2 fix (UI audit 2026-05-10): candidate detail dialog
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [st, srcs, cands, sigs] = await Promise.all([
        getPretenderStats(),
        listPretenderSources(),
        listPretenderCandidates({ page_size: 24 }),
        listPretenderSignals({ since_days: 30, page_size: 50 }),
      ]);
      setStats(st);
      setSources(srcs.items || []);
      setCandidates(cands.items || []);
      setSignals(sigs.items || []);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Deep-link z Feeda: /pre-tender?candidate=<id> → auto-otwórz szczegóły
  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get('candidate');
    if (cid) {
      setSelectedCandidateId(cid);
      setDialogOpen(true);
    }
  }, []);

  const handleCollect = async () => {
    setCollecting(true);
    try {
      toast.info('Zbieram sygnały ze wszystkich źródeł...');
      const r = await collectPretenderSignals({});
      toast.success(`Zebrano ${r.collected} sygnałów, zapisano ${r.saved} (duplikaty: ${r.duplicates_skipped}). Kandydatów: ${r.candidates_updated}`);
      await fetch();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setCollecting(false);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radar className="w-6 h-6 text-primary" />
            Pre-Tender Intelligence
          </h1>
          <p className="text-sm text-muted-foreground">
            Wykrywanie inwestycji przed ogłoszeniem przetargu · plany BZP + projekty + media
          </p>
        </div>
      </div>

      <StatsCards stats={stats} />

      <SourcesPanel sources={sources} onRefresh={handleCollect} collecting={collecting} />

      <Tabs defaultValue="candidates">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="candidates">
            Kandydaci inwestycji {candidates.length > 0 && `(${candidates.length})`}
          </TabsTrigger>
          <TabsTrigger value="signals">
            Feed sygnałów {signals.length > 0 && `(${signals.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="candidates" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-40" />)}
            </div>
          ) : candidates.length === 0 ? (
            <Card>
              <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
                <Target className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Brak kandydatów. Kliknij "Zbierz teraz" aby uruchomić źródła.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {candidates.map(c => (
                <CandidateCard
                  key={c.id}
                  cand={c}
                  onClick={(cand) => {
                    setSelectedCandidateId(cand.id);
                    setDialogOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="signals" className="mt-4">
          <Card>
            {loading ? (
              <CardContent className="pt-6">
                {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 mb-2" />)}
              </CardContent>
            ) : signals.length === 0 ? (
              <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
                <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Brak sygnałów. Kliknij "Zbierz teraz".</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Typ</TableHead>
                    <TableHead>Tytuł / Podmiot</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Źródło</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signals.map(sig => <SignalRow key={sig.id} sig={sig} />)}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Candidate detail dialog — BUG #2 fix (2026-05-10) + materialization (2026-05-11) */}
      <CandidateDetailDialog
        candidateId={selectedCandidateId}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}
